import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from 'styled-components';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { queryClient } from './query/queryClient';
import { GlobalStyle } from './styles/GlobalStyle';
import { theme } from './styles/theme';
import './i18n';
import { canTrackAnalytics, CONSENT_CHANGE_EVENT } from './utils/consent';
import { installAdminSessionGuard } from './utils/auth/adminSessionGuard';

// Antes de qualquer render: uma tela de admin busca dados já ao montar, e é
// justamente essa primeira chamada que descobre a sessão vencida.
installAdminSessionGuard();

/**
 * Componente wrapper para inicializar Amplitude pós-hidratação
 * Evita bloquear o caminho crítico (Core Web Vitals)
 */
function AnalyticsInitializer({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    const initIfConsented = () => {
      if (!canTrackAnalytics()) return;

      // Lazy import para não carregar a SDK no server
      import('./services/analytics/amplitude').then(({ initAmplitude }) => {
        initAmplitude();
      });
    };

    initIfConsented();

    window.addEventListener(CONSENT_CHANGE_EVENT, initIfConsented);
    return () => {
      window.removeEventListener(CONSENT_CHANGE_EVENT, initIfConsented);
    };
  }, []);

  return <>{children}</>;
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    <AnalyticsInitializer>
      <ThemeProvider theme={theme}>
        <GlobalStyle />
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>
      </ThemeProvider>
    </AnalyticsInitializer>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
